import { matchPath } from "react-router-dom";

import { ROUTE_PATTERNS } from "@/lib/analytics/route-patterns";

/** Recorded when a path matches no known route. Never the raw path. */
export const UNKNOWN_ROUTE = "/unknown";

/** Recorded for an API segment that is not a static literal. */
export const PARAM_PLACEHOLDER = ":param";

/**
 * Maps a concrete pathname onto the route pattern that renders it.
 *
 * Returns UNKNOWN_ROUTE when nothing matches, rather than falling back to the
 * raw path — the whole point is that a value can never reach the event store,
 * and an unrecognised path is exactly the case where we don't know what the
 * segments contain.
 */
export function toRoutePattern(pathname: string): string {
  for (const pattern of ROUTE_PATTERNS) {
    if (matchPath({ path: pattern, end: true }, pathname)) return pattern;
  }
  return UNKNOWN_ROUTE;
}

/**
 * Reduces an API URL to a shape safe to store.
 *
 * Query string is dropped wholesale — it carries tokens, filters and RUCs.
 * Then any segment that is not a plain lowercase literal becomes `:param`.
 * That inverted rule (allow static words, replace everything else) is what
 * makes this safe: a uuid, a RUC like `220264030017`, a period like `2026-08`,
 * or an invitation token all fail the literal test and are replaced, whereas a
 * regex hunting for uuids would have passed the last three straight through.
 */
export function toApiPattern(url: string): string {
  const withoutQuery = url.split("?")[0] ?? "";
  const withoutOrigin = withoutQuery.replace(/^https?:\/\/[^/]+/i, "");
  const segments = withoutOrigin.split("/").filter(Boolean);
  const safe = segments.map((segment) =>
    /^[a-z][a-z-]*$/.test(segment) ? segment : PARAM_PLACEHOLDER,
  );
  return `/${safe.join("/")}`;
}
